import Vue from 'vue';
import { isEqual } from 'lodash';

/**
 * This is a base vue mixin meant to be used with components that are going to
 * connect to a clockvine API via RESTful methods.
 */
export default {
    props: ['id'],

    data: () => ({
        errors: {},
        primaryKey: 'id',
        record: {},
        saving: false,
        loading: false,
        initialRecord: {}
    }),

    computed: {
        exists() {
            return this.id || this.record[this.primaryKey];
        },

        storedRecord() {
            return this.getStoredRecord();
        },

        hasChanges() {
            return this.isDirty();
        },
    },

    created() {
        this.record[this.primaryKey] = this[this.primaryKey];
    },

    mounted() {
        this.initialRecord = JSON.parse(JSON.stringify(this.getRecord()));

        if (this.exists) {
            this.loading = true;

            return this.$store.dispatch(this.type + '/show', this.getPrimaryKeyParams())
                .then(() => {
                    this.reset();
                    this.loading = false;
                })
                .catch(() => {
                    this.loading = false;
                });
        }
    },

    methods: {
        getStoredRecord() {
            return this.$store.state[this.type].records[this.record[this.primaryKey]];
        },

        reset() {
            this.record = (this.getStoredRecord())
                ? JSON.parse(JSON.stringify(this.getStoredRecord()))
                : JSON.parse(JSON.stringify(this.initialRecord));
        },

        isDirty() {
            return !isEqual(this.getStoredRecord(), this.getRecord());
        },

        handleErrors(error) {
            let errors = error.response.data.errors;

            if (Array.isArray(errors) && errors.length == 1) {
                this.errors = errors[0];
            } else {
                this.errors = error.response.data.errors;
            }

            this.saving = false;

            return Promise.reject(error.response);
        },

        save(force = false) {
            this.saving = true;

            if (this.exists) {
                if (!this.hasChanges && !force) {
                    this.saving = false;
                    return Promise.resolve();
                }

                return this.$store.dispatch(this.type + '/update', this.getRecordParams())
                    .then((r) => {
                        this.saving = false;
                        this.reset();
                        return Promise.resolve(r);
                    })
                    .catch(this.handleErrors);
            } else {
                return this.$store.dispatch(this.type + '/store', this.getRecordParams())
                    // don't love how this uses the actual response...
                    .then((r) => {
                        this.record[this.primaryKey] = r.data.data.id;
                        this.reset();
                        this.saving = false;
                        return Promise.resolve(r);
                    })
                    .catch(this.handleErrors);
            }
        },

        copy() {
            return this.$store.dispatch(this.type + '/copy', this.getPrimaryKeyParams())
                .catch(this.handleErrors);
        },

        destroy() {
            return this.$store.dispatch(this.type + '/destroy', this.getPrimaryKeyParams())
                .catch(this.handleErrors);
        },

        getRecord() {
            return this.record;
        },

        getUrlParams() {
            return {};
        },

        getRecordParams() {
            return Object.assign({}, this.getUrlParams(), { data: this.getRecord() });
        },

        getPrimaryKeyParams() {
            return Object.assign({}, this.getUrlParams(), { [this.primaryKey]: this.record[this.primaryKey] });
        },
    },
};
